import { useState, type FormEvent } from 'react';
import { createBaby, ensureHousehold, updateBaby } from '@/db/repo';
import { db, META } from '@/db/db';
import { DEFAULT_TZ, useApp } from '@/app/AppContext';
import type { LocalBaby } from '@/lib/schemas';

interface Props { baby?: LocalBaby; onboarding?: boolean; onDone: () => void }

export function ProfileForm({ baby, onboarding = false, onDone }: Props) {
  const { household, setActiveBaby } = useApp();
  const [name, setName] = useState(baby?.display_name ?? '');
  const [householdName, setHouseholdName] = useState(household?.name ?? 'My household');
  const [dob, setDob] = useState(baby?.date_of_birth ?? '');
  const [timezone, setTimezone] = useState(baby?.timezone ?? DEFAULT_TZ);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    try {
      const input = { display_name: name, date_of_birth: dob || null, timezone: timezone.trim() };
      if (onboarding && !householdName.trim()) throw new Error('Enter a household name');
      // Household, baby and active selection either commit together or roll back.
      await db.transaction('rw', [db.meta, db.babies, db.events, db.outbox], async () => {
        const record = baby ? await updateBaby(baby.id, input) : await createBaby(input);
        if (onboarding) {
          const h = await ensureHousehold();
          await db.meta.put({ key: META.household, value: { ...h, name: householdName.trim() } });
        }
        await setActiveBaby(record.id);
      });
      onDone();
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not save profile. Please try again.'); }
    finally { setBusy(false); }
  }

  return <form onSubmit={submit} className="space-y-4" aria-label={baby ? 'Edit baby profile' : 'New baby profile'}>
    {onboarding && <div><label className="label" htmlFor="household-name">Household name</label><input id="household-name" className="input" value={householdName} maxLength={80} onChange={e => setHouseholdName(e.target.value)} required /></div>}
    <div><label className="label" htmlFor="baby-name">Baby name</label><input autoFocus id="baby-name" className="input" value={name} maxLength={80} onChange={e => setName(e.target.value)} required /></div>
    <div><label className="label" htmlFor="baby-dob">Date of birth (optional)</label><input id="baby-dob" className="input" type="date" value={dob} onChange={e => setDob(e.target.value)} /></div>
    <div><label className="label" htmlFor="baby-timezone">Timezone</label><input id="baby-timezone" className="input" list="timezones" value={timezone} onChange={e => setTimezone(e.target.value)} required /><datalist id="timezones">{['Australia/Sydney', 'Australia/Brisbane', 'Australia/Perth', 'Pacific/Auckland', 'Europe/London', 'America/New_York', 'UTC'].map(tz => <option key={tz} value={tz} />)}</datalist><p className="mt-1 text-xs text-ink-300">Use an IANA timezone, such as Australia/Sydney.</p></div>
    {error && <p role="alert" className="text-danger">{error}</p>}
    <button className="btn-primary w-full" disabled={busy}>{busy ? 'Saving…' : baby ? 'Save profile' : 'Create baby profile'}</button>
  </form>;
}
